import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { query, where, orderBy, Timestamp, getDoc } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useSchool } from '../../contexts/SchoolContext';
import { useCollection } from '../../hooks/useFirestore';
import { leaveRequestsCol, policiesCol, onCallAlertsCol, optionChoicesCol, optionsConfigDoc } from '../../lib/firestore';
import { getWellbeingDashboard, getAssessmentInsightsOverview } from '../../lib/functions';
import type { LeaveRequest, Policy, OnCallAlert, OptionChoice } from '../../types';

function startOfTodayUTC(): Date {
  const d = new Date();
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

type Tone = 'neutral' | 'attention' | 'good';

const TONE: Record<Tone, { ring: string; value: string }> = {
  neutral:   { ring: 'border-gray-200',  value: 'text-gray-900' },
  attention: { ring: 'border-amber-300', value: 'text-amber-600' },
  good:      { ring: 'border-green-200', value: 'text-green-700' },
};

function StatTile({
  icon, label, value, sub, to, tone = 'neutral',
}: {
  readonly icon: string;
  readonly label: string;
  readonly value: React.ReactNode;
  readonly sub: string;
  readonly to: string;
  readonly tone?: Tone;
}) {
  return (
    <Link
      to={to}
      className={`card flex items-center gap-4 border p-4 transition-shadow hover:shadow-md ${TONE[tone].ring}`}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-50 text-xl" aria-hidden>
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</span>
        <span className={`block text-xl font-bold leading-tight ${TONE[tone].value}`}>{value}</span>
        <span className="block text-xs text-gray-500">{sub}</span>
      </span>
    </Link>
  );
}

/**
 * At-a-glance summary for school leadership — the actionable state of every
 * module the school holds, surfaced the moment they sign in. Each widget is
 * gated on the relevant module; widgets with nothing to report stay quiet.
 */
export default function LeadershipSummary() {
  const { appUser } = useAuth();
  const { school, hasModule } = useSchool();
  const schoolId = appUser?.schoolId ?? '';
  const today = startOfTodayUTC();

  // ── Leave & Cover (cheap Firestore reads) ───────────────────────────────
  const { data: rawPending } = useCollection<LeaveRequest>(
    schoolId && hasModule('leave')
      ? query(leaveRequestsCol, where('schoolId', '==', schoolId),
          where('status', 'in', ['pendingLineManager', 'pendingSlt']))
      : null,
    `${schoolId}:pendingLeave`,
  );
  const pendingLeave = (rawPending as LeaveRequest[]).length;

  // ── Policies ─────────────────────────────────────────────────────────────
  const { data: rawPolicies } = useCollection<Policy>(
    schoolId && hasModule('policies')
      ? query(policiesCol, where('schoolId', '==', schoolId), orderBy('nextReviewDate', 'asc'))
      : null,
    `${schoolId}:policies`,
  );
  const { overduePolicies, dueSoonPolicies } = useMemo(() => {
    const now = Date.now();
    const active = (rawPolicies as Policy[]).filter((p) => p.status === 'active');
    return {
      overduePolicies: active.filter((p) => p.nextReviewDate.toDate().getTime() < now).length,
      dueSoonPolicies: active.filter((p) => {
        const t = p.nextReviewDate.toDate().getTime();
        return t >= now && t < now + 30 * 86_400_000;
      }).length,
    };
  }, [rawPolicies]);

  // ── Wellbeing (callable) ─────────────────────────────────────────────────
  const [wellbeing, setWellbeing] = useState<{ avg: number | null; responses: number } | null>(null);
  useEffect(() => {
    if (!schoolId || !hasModule('wellbeing')) return;
    let cancelled = false;
    getWellbeingDashboard({})
      .then((res) => {
        if (cancelled) return;
        const latest = [...res.data.weeks].reverse().find((w) => !w.belowThreshold);
        setWellbeing(latest ? { avg: latest.averageScore, responses: latest.responses } : { avg: null, responses: 0 });
      })
      .catch((err) => { console.error('[Home] wellbeing summary failed:', err); /* widget hidden on error */ });
    return () => { cancelled = true; };
  }, [schoolId, hasModule]);

  // ── Assessment Insights (callable) ───────────────────────────────────────
  const [insights, setInsights] = useState<{ avg: number | null; fully: number; total: number } | null>(null);
  useEffect(() => {
    if (!schoolId || !hasModule('insights')) return;
    let cancelled = false;
    getAssessmentInsightsOverview({})
      .then((res) => {
        if (cancelled) return;
        setInsights({ avg: res.data.schoolAveragePercent, fully: res.data.fullyMarkedCount, total: res.data.assessmentCount });
      })
      .catch((err) => { console.error('[Home] insights summary failed:', err); /* hidden on error */ });
    return () => { cancelled = true; };
  }, [schoolId, hasModule]);

  // ── Behaviour (open on-call alerts — the most urgent signal) ─────────────
  const { data: rawAlerts } = useCollection<OnCallAlert>(
    schoolId && hasModule('behaviour')
      ? query(onCallAlertsCol, where('schoolId', '==', schoolId))
      : null,
    `${schoolId}:oncall`,
  );
  const openOnCall = (rawAlerts as OnCallAlert[]).filter((a) => a.status === 'open').length;

  // ── KS4 Options (open round — how many pupils have submitted) ─────────────
  const { data: rawChoices } = useCollection<OptionChoice>(
    schoolId && hasModule('options')
      ? query(optionChoicesCol, where('schoolId', '==', schoolId))
      : null,
    `${schoolId}:optionChoices`,
  );
  const submittedChoices = (rawChoices as OptionChoice[]).length;
  const [optionsState, setOptionsState] = useState<{ open: boolean; deadline: string | null } | null>(null);
  useEffect(() => {
    if (!schoolId || !hasModule('options')) return;
    let cancelled = false;
    getDoc(optionsConfigDoc(schoolId))
      .then((s) => { if (!cancelled) setOptionsState(s.exists() ? { open: !!s.data().open, deadline: s.data().deadline ?? null } : null); })
      .catch((err) => { console.error('[Home] options summary failed:', err); /* hidden on error */ });
    return () => { cancelled = true; };
  }, [schoolId, hasModule]);

  // Build the tile list — only widgets with something to show
  const tiles: React.ReactNode[] = [];

  if (hasModule('leave')) {
    tiles.push(
      <StatTile
        key="leave"
        icon="🗓️"
        label="Leave to approve"
        value={pendingLeave}
        sub={pendingLeave === 0 ? 'Nothing waiting' : 'Awaiting a decision'}
        to="/leave/approvals"
        tone={pendingLeave > 0 ? 'attention' : 'neutral'}
      />,
    );

  }

  if (hasModule('policies')) {
    const total = overduePolicies + dueSoonPolicies;
    tiles.push(
      <StatTile
        key="policies"
        icon="📜"
        label="Policy reviews"
        value={total}
        sub={
          overduePolicies > 0
            ? `${overduePolicies} overdue${dueSoonPolicies ? `, ${dueSoonPolicies} due soon` : ''}`
            : dueSoonPolicies > 0 ? 'Due within 30 days' : 'All up to date'
        }
        to="/policies"
        tone={overduePolicies > 0 ? 'attention' : 'neutral'}
      />,
    );
  }

  if (hasModule('wellbeing') && wellbeing) {
    tiles.push(
      <StatTile
        key="wellbeing"
        icon="💙"
        label="Staff wellbeing"
        value={wellbeing.avg !== null ? `${wellbeing.avg.toFixed(1)}/5` : '—'}
        sub={wellbeing.avg !== null ? `${wellbeing.responses} responses this week` : 'Awaiting enough responses'}
        to="/wellbeing/dashboard"
        tone={wellbeing.avg !== null && wellbeing.avg < 3 ? 'attention' : 'good'}
      />,
    );
  }

  if (hasModule('insights') && insights && insights.total > 0) {
    tiles.push(
      <StatTile
        key="insights"
        icon="📊"
        label="Assessment"
        value={insights.avg !== null ? `${Math.round(insights.avg)}%` : '—'}
        sub={`${insights.fully}/${insights.total} fully marked`}
        to="/insights"
        tone="neutral"
      />,
    );
  }

  // Behaviour — only when there's a live on-call alert needing attention.
  if (hasModule('behaviour') && openOnCall > 0) {
    tiles.push(
      <StatTile
        key="oncall"
        icon="🆘"
        label="On-call"
        value={openOnCall}
        sub={openOnCall === 1 ? 'Alert open now' : 'Alerts open now'}
        to="/behaviour/on-call"
        tone="attention"
      />,
    );
  }

  // KS4 Options — only while a round is open.
  if (hasModule('options') && optionsState?.open) {
    const deadlineSoon = optionsState.deadline
      ? (() => { const t = new Date(`${optionsState.deadline}T23:59:59`).getTime() - Date.now(); return t > 0 && t < 14 * 86_400_000; })()
      : false;
    tiles.push(
      <StatTile
        key="options"
        icon="🧩"
        label="KS4 options"
        value={submittedChoices}
        sub={optionsState.deadline ? `submitted · closes ${optionsState.deadline}` : 'submitted so far'}
        to="/options/manage"
        tone={deadlineSoon ? 'attention' : 'neutral'}
      />,
    );
  }

  if (tiles.length === 0) return null;

  return (
    <div className="mb-8">
      <h2 className="mb-3 text-sm font-semibold text-gray-700">{school?.name ?? 'Your school'} today</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{tiles}</div>
    </div>
  );
}
